import React, { useMemo } from 'react'

import { MenuEntryFragmentFragment, MobileMenuFragmentFragment } from '../../../../graphql/gatsby'
import { useSanityRoleAndContextBasedContent } from '../../../../lib/sanity'
import { filterMenuEntriesToDisplay } from '../../../../utils/sanity/documents/menuEntry'
import { MenuAccordionPropsItem } from '../MenuAccordion'
import { MenuAccordionCollapse } from '../MenuAccordionCollapse'
import MobileMenuEntrySeeAllLink from '../MobileMenuEntrySeeAllLink'
import * as Styled from './styled'

type MobileMenuEntries = NonNullable<MobileMenuFragmentFragment['mobileMenu']>
type MobileSubMenuEntryLevel1 = NonNullable<
  NonNullable<MobileMenuEntries[number]>['childEntries']
>[number]
type MobileSubMenuEntryLevel2 = NonNullable<
  NonNullable<MobileSubMenuEntryLevel1>['childEntries']
>[number]
type MobileSubMenuEntryLevel3 = NonNullable<
  NonNullable<MobileSubMenuEntryLevel2>['childEntries']
>[number]
type MobileSubMenuEntries = Array<
  MobileSubMenuEntryLevel1 | MobileSubMenuEntryLevel2 | MobileSubMenuEntryLevel3
>

export interface MobileMenuSubMenuProps {
  className?: string
  menuEntries: MobileSubMenuEntries
  seeAllLink?: MenuEntryFragmentFragment['link']
}

const isMidLevelEntry = (
  entry: MobileSubMenuEntryLevel1 | MobileSubMenuEntryLevel2 | MobileSubMenuEntryLevel3,
): entry is MobileSubMenuEntryLevel1 | MobileSubMenuEntryLevel2 => {
  return !!entry && 'childEntries' in entry
}

export const MobileMenuSubMenu: React.FC<MobileMenuSubMenuProps> = ({
  className,
  menuEntries,
  seeAllLink,
}) => {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const subMenuItems = useMemo(() => {
    const filterMenuEntries = filterMenuEntriesToDisplay(shouldDisplayContent)
    return menuEntries.reduce(
      (acc: MenuAccordionPropsItem[], entry) => {
        if (!entry) return acc
        const { name: entryName } = entry
        const childEntries =
          entry && isMidLevelEntry(entry) ? filterMenuEntries(entry.childEntries) : undefined
        if (!entryName) return acc
        if (childEntries && childEntries.length > 0) {
          acc.push({
            type: 'panel',
            key: entryName,
            toggleElement: (
              <Styled.SubMenuAccordionToggle>{entryName}</Styled.SubMenuAccordionToggle>
            ),
            collapseElement: (
              <MenuAccordionCollapse>
                <MobileMenuSubMenu menuEntries={childEntries} seeAllLink={entry.link} />
              </MenuAccordionCollapse>
            ),
          })
        } else {
          acc.push({
            type: 'element',
            key: entryName,
            element: (
              <Styled.SubMenuEntryLink link={entry.link}>{entryName}</Styled.SubMenuEntryLink>
            ),
          })
        }
        return acc
      },
      seeAllLink
        ? [
            {
              type: 'element',
              key: 'SEE ALL',
              element: (
                <MobileMenuEntrySeeAllLink link={seeAllLink}>SEE ALL</MobileMenuEntrySeeAllLink>
              ),
            },
          ]
        : [],
    )
  }, [menuEntries, seeAllLink, shouldDisplayContent])
  return (
    <Styled.SubMenuAccordion
      className={className}
      role="tablist"
      aria-multiselectable
      items={subMenuItems}
    />
  )
}
